import React, { useEffect, useState } from 'react';
import Event from '@/model/Event';
import Page from '@/model/Page';
import validators from '@/helpers/validators';
import { useDialog } from '@/hooks/useDialog';
import useForm from '@/hooks/useForm';
import api, { AxiosError } from '@/services/api';
import { toast } from 'react-toastify';
import { States } from '@/helpers/common/states';
import { useHistory } from 'react-router-dom';
import { FormInputFilterEvent } from '../../types';
import { EventCancelTicketsUI, ShouldShowModal } from './ui';

export const EventCancelTicketsScreen: React.FC = (): JSX.Element => {
  const [currentPage, setCurrentPage] = useState<Page<Event, Event>>({
    page: 1,
    pageSize: 10,
    sort: 'startDate',
    order: 'DESC',
  });
  const [state, setState] = useState<States>(States.default);
  const [shouldShowModal, setShouldShowModal] = useState<ShouldShowModal>(ShouldShowModal.filter);
  const { title, visible, onChangeTitle, onToggle } = useDialog();
  const history = useHistory();

  const {
    formData: formDataFilter,
    formErrors: formErrorsFilter,
    onChangeFormInput: onChangeFormInputFilter,
    isFormValid: isFormValidFilter,
    resetForm: resetFormFilter,
  } = useForm({
    initialData: {
      filterSearch: '',
      inputSearch: '',
    },
    validators: {
      filterSearch: [validators.required],
      inputSearch: [validators.required],
    },
  });

  const handleOnPage = async (page: Page<Event, Event>): Promise<void> => {
    try {
      setState(States.loading);
      const { data } = await api.post<Page<Event, Event>>('/event/page', page);
      if (data) {
        setCurrentPage(data);
      }
    } catch (error) {
      const err = error as AxiosError;
      if (err.response?.status !== 401) {
        toast.error(err.message);
      }
    } finally {
      setState(States.default);
    }
  };
  const handleClearFilter = async (): Promise<void> => {
    resetFormFilter();
    const newPage: Page<Event, Event> = {
      page: 1,
      pageSize: 10,
      sort: 'startDate',
      order: 'ASC',
    };
    onToggle();
    await handleOnPage(newPage);
  };

  const handleOnPagination = (pageNumber: number): void => {
    const newPage: Page<Event, Event> = {
      ...currentPage,
      page: pageNumber,
    };
    handleOnPage(newPage);
  };

  const handleGoToCancel = (id: string): void => {
    history.push(`/support/cancel-ticket/${id}`);
  };
  const handleOnFilter = async (): Promise<void> => {
    if (isFormValidFilter()) {
      const entity = {} as Event;
      entity.name = formDataFilter[FormInputFilterEvent.inputSearch];

      const newPage: Page<Event, Event> = {
        page: 1,
        pageSize: 10,
        entity,
        sort: 'startDate',
        order: 'DESC',
        total: 1,
      };
      onToggle();
      await handleOnPage(newPage);
    }
  };

  const handleOnShouldShowModal = async ({
    value,
    newTitleModal,
  }: {
    value: ShouldShowModal;
    newTitleModal: string | React.ReactNode;
  }): Promise<void> => {
    setShouldShowModal(value);
    onChangeTitle(newTitleModal);
    onToggle();
  };

  useEffect(() => {
    handleOnPage(currentPage);
  }, []);

  return (
    <EventCancelTicketsUI
      state={state}
      title={title}
      visible={visible}
      shouldShowModal={shouldShowModal}
      onShouldShowModal={handleOnShouldShowModal}
      onToggle={onToggle}
      currentPage={currentPage}
      formData={formDataFilter}
      goTocancel={handleGoToCancel}
      onChangeFormInput={onChangeFormInputFilter}
      formErrors={formErrorsFilter}
      clearFilter={handleClearFilter}
      onPagination={handleOnPagination}
      onFilter={handleOnFilter}
    />
  );
};
